import { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Toggle = ({ checked, onChange, testid }) => (
  <button data-testid={testid} onClick={() => onChange(!checked)} role="switch" aria-checked={checked}
    className={`relative h-5 w-9 shrink-0 rounded-full shadow-sm transition-colors ${checked ? "bg-redline-500" : "bg-sand-300"}`}>
    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
  </button>
);

const SWITCHES = [
  ["photo_required_default", "New tasks require a photo",
    "Every task you add starts out needing photo proof. You can still turn it off task by task."],
  ["mark_missed", "Mark a visit missed when the window closes",
    "Nothing ticked by the deadline files a missed record and texts the vendor's office. Turn it off and late visits simply stay open. A checklist can overrule this for itself."],
  ["auto_approve", "Approve automatically when everything is ticked and photographed",
    "Off means a person always signs off. On lets routine lists close themselves — anything short of a clean sweep still waits for you."],
  ["board_can_view", "Board members can open a visit you share",
    "Read-only, and only for visits you explicitly share. Board members can never comment to the vendor."],
  ["crew_can_add", "The crew can add items to a list",
    "The default for new checklists. On site they choose “just today” or “every time”, and anything they keep shows on your record with a one-tap undo. Each checklist can overrule this — leave it off for lists nobody should touch, like pool chemicals."],
];

export function Settings({ tenantId, config, onSaved, readOnly }) {
  const [cfg, setCfg] = useState(config);
  const [busy, setBusy] = useState(false);
  // The page can render from cached visits before /checklists/config resolves — without this the
  // panel stays blank until the tab is remounted.
  useEffect(() => { if (config) setCfg(config); }, [config]);
  if (!cfg) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/tenants/${tenantId}/checklists/config`, cfg);
      if (data.config) setCfg(data.config);
      onSaved?.(data.config);
      toast.success("Checklist settings saved");
    } catch { toast.error("Couldn't save"); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-sand-200 bg-white p-6" data-testid="checklists-settings">
      <h3 className="font-heading text-base font-bold tracking-tight">Defaults for this community</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Where a checklist doesn't say otherwise, these apply. Nothing assumes mornings — a reminder is tied to the
        service window or the deadline, because vendors turn up whenever they turn up.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm font-semibold">Done by, when a checklist doesn't set one</label>
          <p className="text-xs text-muted-foreground">The deadline a visit is judged against.</p>
          <input type="time" data-testid="settings-due-by" disabled={readOnly} value={cfg.due_by || "17:00"}
            onChange={(e) => setCfg({ ...cfg, due_by: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
        </div>
        <div>
          <label className="text-sm font-semibold">Reminder to the vendor</label>
          <p className="text-xs text-muted-foreground">When their link gets texted out for the day.</p>
          <select data-testid="settings-remind" disabled={readOnly} value={cfg.remind || "window_start"}
            onChange={(e) => setCfg({ ...cfg, remind: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-sand-200 bg-white px-3.5 py-2.5 text-sm outline-none">
            <option value="window_start">When the service window opens</option>
            <option value="hours_before">A few hours before the deadline</option>
            <option value="off">Don't remind them at all</option>
          </select>
        </div>
        {cfg.remind === "hours_before" && (
          <div>
            <label className="text-sm font-semibold">How many hours before</label>
            <p className="text-xs text-muted-foreground">Counted back from the done-by time.</p>
            <input type="number" min="1" max="12" data-testid="settings-remind-hours" disabled={readOnly} value={cfg.remind_hours ?? 2}
              onChange={(e) => setCfg({ ...cfg, remind_hours: e.target.value })}
              className="mt-1.5 w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none" />
          </div>
        )}
        <div>
          <label className="text-sm font-semibold">Nudge if nothing has been ticked</label>
          <p className="text-xs text-muted-foreground">Minutes before the deadline. Goes to the vendor only — you aren't paged for a late start.</p>
          <input type="number" min="0" max="480" data-testid="settings-nudge" disabled={readOnly} value={cfg.nudge_minutes ?? 60}
            onChange={(e) => setCfg({ ...cfg, nudge_minutes: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none" />
        </div>
        <div>
          <label className="text-sm font-semibold">Who gets the texts</label>
          <p className="text-xs text-muted-foreground">Overridden per vendor on the checklist itself.</p>
          <select data-testid="settings-notify" disabled={readOnly} value={cfg.notify_to || "both"}
            onChange={(e) => setCfg({ ...cfg, notify_to: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-sand-200 bg-white px-3.5 py-2.5 text-sm outline-none">
            <option value="both">The vendor's office and the person on site</option>
            <option value="office">The vendor's office only</option>
            <option value="onsite">The person on site only</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold">Build the schedule this far ahead</label>
          <p className="text-xs text-muted-foreground">How much of the calendar exists as real visits you can see and excuse in advance.</p>
          <select data-testid="settings-horizon" disabled={readOnly} value={String(cfg.horizon_days ?? 14)}
            onChange={(e) => setCfg({ ...cfg, horizon_days: Number(e.target.value) })}
            className="mt-1.5 w-full rounded-xl border border-sand-200 bg-white px-3.5 py-2.5 text-sm outline-none">
            <option value="7">1 week</option>
            <option value="14">2 weeks</option>
            <option value="30">1 month</option>
            <option value="90">3 months</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold">Fill in gaps this far back</label>
          <p className="text-xs text-muted-foreground">
            Days the system was unreachable are rebuilt and closed out, so a gap can never quietly erase a record. In days.
          </p>
          <input type="number" min="0" max="30" data-testid="settings-backfill" disabled={readOnly} value={cfg.backfill_days ?? 7}
            onChange={(e) => setCfg({ ...cfg, backfill_days: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none" />
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {SWITCHES.map(([k, label, hint]) => (
          <label key={k} className="flex cursor-pointer items-start gap-3">
            <span className="pt-0.5"><Toggle testid={`settings-${k}`} checked={!!cfg[k]} onChange={(v) => !readOnly && setCfg({ ...cfg, [k]: v })} /></span>
            <span>
              <span className="block text-sm font-semibold">{label}</span>
              <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
          </label>
        ))}
      </div>

      {!readOnly && (
        <button onClick={save} disabled={busy} data-testid="settings-save"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-redline-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-redline-600 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
        </button>
      )}
    </div>
  );
}
